import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
} from "@mui/material";
import moment from "moment";
import { Setting } from "../../../../../../utils/Setting";
import { getApiData } from "../../../../../../utils/APIHelper";
import { toast } from "react-toastify";
import { ReactComponent as NewDesignIcon } from "../../../../../../assets/svg/NewDesignIcon.svg";
import { ReactComponent as NewRenovationIcon } from "../../../../../../assets/svg/NewRenovationIcon.svg";
import { milestoneStatusStyleArray } from "../../../../../../pages/NewProjectDetails/config";
import { parseDesignSnapshot, getStatusDisplayText } from "./designUtils";
import {
  milestoneCrTablePaperSx,
  milestoneCrHeadCellSx,
  milestoneCrCheckboxSx,
  milestoneCrStatusDotSx,
} from "../shared/milestoneCrTableStyles";

const DesignMilestones = ({ data }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const snapshot = useMemo(() => parseDesignSnapshot(data?.snapshot), [data?.snapshot]);
  const snapshotMilestones = snapshot?.milestones || [];

  const snapshotIds = useMemo(
    () => new Set(snapshotMilestones.map((m) => m.id).filter((id) => id != null)),
    [snapshotMilestones]
  );

  useEffect(() => {
    const projectId = data?.projectId;
    if (!projectId) return;

    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await getApiData(
          Setting.endpoints.milestoneDesigns(projectId),
          "GET",
          {},
          {},
          true
        );
        if (cancelled) return;
        const list = Array.isArray(res) ? res : res?.data ?? [];
        setRows(Array.isArray(list) ? list : []);
      } catch (err) {
        if (!cancelled) {
          toast.error(err.message || "Failed to load milestones");
          setRows([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [data?.projectId]);

  const isChecked = (milestoneId) => snapshotIds.has(milestoneId);

  return (
    <Box sx={{ mt: 2, width: "100%" }}>
      <Typography sx={{ fontSize: "14px", color: "#6b7280", mb: 3, fontWeight: 400 }}>
        Select milestones affected by this design change.
      </Typography>

      <TableContainer component={Paper} elevation={0} sx={milestoneCrTablePaperSx}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 52, ...milestoneCrHeadCellSx }} />
              <TableCell sx={milestoneCrHeadCellSx}>Milestone Name</TableCell>
              <TableCell sx={milestoneCrHeadCellSx}>Start date</TableCell>
              <TableCell sx={milestoneCrHeadCellSx}>End date</TableCell>
              <TableCell sx={milestoneCrHeadCellSx}>Tags</TableCell>
              <TableCell sx={milestoneCrHeadCellSx}>Milestone Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: "#6b7280" }}>
                  Loading milestones…
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              rows.map((item) => {
                const m = item?.milestone;
                if (!m) return null;
                const mid = m.milestoneId;
                const checked = isChecked(mid);
                const tagStr = m.tags || "—";
                const statusBg =
                  milestoneStatusStyleArray.find(
                    (style) =>
                      style.status?.toLowerCase() === String(m.status || "").toLowerCase()
                  )?.backgroundColor || "#fa8c16";

                return (
                  <TableRow key={mid ?? m.milestoneName}>
                    <TableCell>
                      <Checkbox
                        checked={checked}
                        disabled
                        disableRipple
                        tabIndex={-1}
                        sx={milestoneCrCheckboxSx}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: "14px", fontWeight: 600, color: "#0d0d0e" }}>
                      {m.milestoneName || "—"}
                    </TableCell>
                    <TableCell sx={{ fontSize: "14px", fontWeight: 500, color: "#0d0d0e" }}>
                      {m.startDate ? moment(m.startDate).format("MMM DD, YYYY") : "—"}
                    </TableCell>
                    <TableCell sx={{ fontSize: "14px", fontWeight: 500, color: "#0d0d0e" }}>
                      {m.endDate ? moment(m.endDate).format("MMM DD, YYYY") : "—"}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap="8px">
                        {m.tags && (
                          <Box display="flex" alignItems="center" sx={{ color: "#949DAA" }}>
                            {m.tags === "Design" ? <NewDesignIcon /> : <NewRenovationIcon />}
                          </Box>
                        )}
                        <Typography sx={{ fontSize: "14px", color: "#0d0d0e", fontWeight: 500 }}>
                          {tagStr}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <Box sx={milestoneCrStatusDotSx(statusBg)} />
                        <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#0d0d0e" }}>
                          {getStatusDisplayText(m.status) || "—"}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: "#6b7280" }}>
                  No milestones available.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default DesignMilestones;
